import React from 'react';
import clsx from 'clsx';
import {Button, Typography} from '@mui/material';
import {Alert} from '@netx/core-components/Alert';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {LegacyFormPasswordField} from '@netx/core-components/Form/PasswordField';
import useForm from '@netx/core-components/Form/hooks/useForm';
import {
	LegacyForm as Form,
	type LegacyFormProps as FormProps,
} from '@netx/core-components/Form/LegacyForm';
import {getInputFormObject} from '@netx/core-components/Form/util';
import {
	ScrollBorder,
	scrollBorderClasses,
} from '@netx/core-components/ScrollBorder';
import {SubmitButton} from '@netx/core-components/SubmitButton';
import {useResetPassword} from '../../hooks';
import {AuthMessage, AuthTitle, AuthActionLabel} from '../../locales';
import type {ResetPasswordFormData} from '../../types';
import {
	resetPasswordClasses,
	getResetPasswordUtilityClass,
	ResetPasswordClasses,
	RESET_PASSWORD_CLASS_NAME,
} from './resetPasswordClasses';

export type ResetPasswordOwnerState = WithClasses<ResetPasswordClasses, {}>;

const useUtilityClasses = (ownerState: ResetPasswordOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getResetPasswordUtilityClass, classes);
};

const ResetPasswordRoot = styled('div', {
	name: RESET_PASSWORD_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	...theme.mixins.flexFillColumn,
	alignItems: 'center',
	width: '100%',
	'.input-field + .input-field': {
		marginTop: theme.spacing(2),
	},
}));

const ResetPasswordHeader = styled('header')(({theme}) => ({
	flex: '0 0 auto',
	padding: '24px 16px',
}));

const ResetPasswordContent = styled(ScrollBorder)(({theme}) => ({
	[`& .${scrollBorderClasses.scrollWrapper}`]: {
		padding: theme.spacing(1, 4, 3, 4),
	},
}));

const ResetPasswordForm = styled(Form)(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	width: '100%',
})) as (props: FormProps<ResetPasswordFormData>) => JSX.Element;

const ResetPasswordFooter = styled('footer')(({theme}) => ({
	flex: '0 0 auto',
	alignItems: 'center',
	display: 'flex',
	width: '100%',
	justifyContent: 'space-between',
	padding: '24px',
}));

const ResetPasswordAlert = styled(Alert, {
	name: RESET_PASSWORD_CLASS_NAME,
	slot: 'Alert',
})(({theme}) => ({
	justifyContent: 'center',
	position: 'relative',
	marginBottom: theme.spacing(2),
	width: '100%',
}));

export type ResetPasswordProps = WithClasses<
	ResetPasswordClasses,
	{
		login: string;
		token: string;
		onCancel?: () => void;
	}
>;

export const ResetPasswordPrimitive = ({
	classes: _classes,
	className,
	login,
	token,
	onCancel,
	...props
}: ResetPasswordProps) => {
	const {resettingPassword, resetPassword, error} = useResetPassword();

	const form = useForm<ResetPasswordFormData>(
		{
			login,
			token,
			password: '',
			password2: '',
		},
		{
			onSubmit: resetPassword,
		},
	);
	const inputForm = getInputFormObject(form);

	const ownerState: ResetPasswordOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<ResetPasswordRoot className={clsx(classes.root, className)} {...props}>
			<ResetPasswordHeader>
				<Typography variant="h5">{AuthTitle.UPDATE_PASSWORD}</Typography>
			</ResetPasswordHeader>

			{error ? (
				<ResetPasswordAlert severity="error">
					{AuthMessage.PASSWORD_UPDATE_FAILED}
				</ResetPasswordAlert>
			) : null}

			<ResetPasswordForm form={form}>
				<ResetPasswordContent>
					<LegacyFormPasswordField
						className={clsx('input-field')}
						name="password"
						disabled={form.isSubmitting}
						error={form.errors.password}
						isNewPassword
						form={inputForm}
						required
						value={form.values.password}
					/>
					<LegacyFormPasswordField
						confirmAgainst="password"
						className={clsx('input-field')}
						disabled={form.isSubmitting}
						error={form.errors.password2}
						isConfirm
						form={inputForm}
						required
						value={form.values.password2}
					/>
				</ResetPasswordContent>

				<ResetPasswordFooter>
					<Button
						disabled={form.isSubmitting}
						variant="text"
						onClick={() => {
							onCancel?.();
						}}
					>
						{AuthActionLabel.RETURN_TO_LOGIN}
					</Button>
					<SubmitButton
						showWorking={form.isSubmitting}
						disabled={!form.isValid}
						label={AuthActionLabel.UPDATE_PASSWORD}
					/>
				</ResetPasswordFooter>
			</ResetPasswordForm>
		</ResetPasswordRoot>
	);
};

export const ResetPassword = ResetPasswordPrimitive;

export default ResetPassword;
